import { ListItem, ListItemText, MenuItem, Stack, TextField, Typography } from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import { AttributeSchemaObjectItem } from "./AttributeSchemaObjectList";
import { useFieldArray, useFormContext } from "react-hook-form";
import { isMobile, isTablet } from "react-device-detect";
import { AttributeSchemaFormType } from "../AttributeSchemaEditorUtils";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from "@mui/x-date-pickers";
import useDebounce from "../../../hooks/use-debounce/UseDebounce";
import { AttributeSchemaTypeEnum } from "../../../services/attribute-data-service/Models";
import FormCheckbox from "../../forms/form-checkbox/FormCheckbox";
import FormSelect from "../../forms/form-select/FormSelect";
import NumberFormatCustom from "../../numeric-format-custom/NumericFormatCustom";
import DefaultDatePicker from "../../default-date-picker/DefaultDatePicker";

export type AttributeSchemaObjectListItemProps = {
    name: 'devices' | 'wells' | 'tanks' | 'tankBatteries',
    item: AttributeSchemaObjectItem,
}

export function AttributeSchemaObjectListItem({
    name,
    item,
}: AttributeSchemaObjectListItemProps) {

    const isPhone = isMobile && !isTablet;
    const [attributeValue, setAttributeValue] = useState<string | number>(item.av);
    const debouncedValue = useDebounce(attributeValue, 200);

    const {
        getValues,
        watch,
        //trigger,
        control,
        //getValues,
        trigger,
        //reset,
        setValue,
        resetField
    } = useFormContext<AttributeSchemaFormType>();    

    const { fields: list,
    // remove, 
    // append 
    } = useFieldArray({
        control,
        name: `${name}`,
        keyName: 'fieldKey'
      });


    // we need the 'real' index of the item
    const index = list.findIndex(o => o.oid === item.oid); 

    const [
        schemaType,
        date,
        valueOptions,
        wholeDigits,
        decimals
        
    ] = watch([
        'schemaType',
        `${name}.${index}.avdate`,
        'valueOptions',
        'wholeDigits',
        'decimalDigits'
    ]);


    const numeric = schemaType === AttributeSchemaTypeEnum.Numeric;

    function onSelect(checked: boolean) {

        if (!checked) {
            setValue(`${name}.${index}.av`, '', {shouldDirty: true});
            setValue(`${name}.${index}.avdate`, null, {shouldDirty: true});

            setAttributeValue('');
        }
    }

    function onDateChanged(value: Date | null) {
        setValue(`${name}.${index}.avdate`, value, {shouldDirty: true});
        const selected = getValues(`${name}.${index}.selected`);
        if (!selected) {
            setValue(`${name}.${index}.selected`, true, {shouldDirty: true});
        }
    }

    function onValueSelected() {

        const selected = getValues(`${name}.${index}.selected`);
        if (!selected) {
            setValue(`${name}.${index}.selected`, true, {shouldDirty: true});
        }

        setValue(`${name}.${index}.av`, '', {shouldDirty: true});
        setAttributeValue('');
    }

    function onValueChanged(newValue: string) {

        let copy = newValue.toString();

        if (numeric && wholeDigits) {
                
            const negative = copy.charAt(0) === '-';
            let str = copy.replace('-', '');
            const ndx = str.indexOf('.');
            if (ndx !== -1) {
                str = str.substring(0, ndx);
            }

            if (str.length > wholeDigits) {
                copy = copy.substring(0, wholeDigits);

                if (negative) {
                    copy = "-" + copy;
                }

                // have to change the value to something different to force an update, otherwise 
                // the new value will not display in the input field
                setAttributeValue(newValue);
            }
           
        }

        setTimeout(() => {

            setAttributeValue(copy);
        });
    }

    function renderSelect(){
       return (
        <FormSelect
            name={`${name}.${index}.avalt`}
            sx={{ 
                //width: 1,
                flexBasis: '50%',
                marginTop: 1,
                marginLeft: 1
            }}
            onChange={() => onValueSelected()}
            >

            <MenuItem sx={{height: '2rem'}} value=''>
                {' '}
            
            </MenuItem>

            {valueOptions.map((o, ndx) => 
                <MenuItem key={ndx} value={o.value}>
                {o.value}
                </MenuItem>
            )}                                
        </FormSelect>
       );
    }


    function renderInput() {
        return (
            <TextField
                variant="filled"
                value={attributeValue ?? ''}
                sx={{ 
                    //width: "100%",
                    marginTop: isPhone && schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ? 0 : 1,
                    flexBasis: '50%',
                    marginLeft: 1,
                    //marginRight: 1
                }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => onValueChanged(event.target.value)}
                slotProps={{
                    input: {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        inputComponent: numeric ? NumberFormatCustom as any : undefined,
                        inputProps: {
                        
                            decimals: decimals,
                            pattern: numeric ? encodeURIComponent(`^[-+]?\\d*\\.?\\d{0,${decimals ?? 2}}$`) : undefined,
                            inputMode: numeric ? decimals ? decimals === 0 ? 'numeric' : 'decimal' : 'decimal' : undefined,
                            maxLength: 128,
                        },
                    },

                    inputLabel: {
                        shrink: true,
                    }
                }} />
        );
    }

    useEffect(() => {


        if (schemaType === AttributeSchemaTypeEnum.FreeformText || 
            schemaType === AttributeSchemaTypeEnum.Numeric ||
            schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) {
            const currentValue = getValues(`${name}.${index}.av`);

            const newValue = debouncedValue.toString();

            if (newValue !== currentValue) {
            
                if (schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) {

                    if (valueOptions.find(o => o.value.toLowerCase() === newValue.toLowerCase())) {
                        setAttributeValue('');
                        setValue(`${name}.${index}.av`, '', { shouldDirty: true });
                        setValue(`${name}.${index}.avalt`, newValue, { shouldDirty: true });    
                    } else {
                        setValue(`${name}.${index}.avalt`, '', { shouldDirty: true });
                        setValue(`${name}.${index}.av`, newValue, { shouldDirty: true });    
                    }
                } else {
                    if (newValue.length > 0) {
                        setValue(`${name}.${index}.avalt`, '', { shouldDirty: true });
                    }

                    setValue(`${name}.${index}.av`, newValue, { shouldDirty: true });
                } 


                setValue(`${name}.${index}.selected`, true, { shouldDirty: true });
            }
        }
       
    }, [
        name,
        index,
        debouncedValue,
        getValues,
        setValue,
        resetField,
        trigger,
        valueOptions,
        schemaType,
        setAttributeValue
    ]);

    return (
        <ListItem
            divider={isPhone && schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ? true : false}
            dense
            sx={{
                margin: 0,
                //paddingLeft: 0,
                //transform: CSS.Transform.toString(transform),
                //transition,
                //opacity: isDragging ? theme => theme.palette.action.disabledOpacity : 1,
                position: 'relative'
            }}
        >
            <ListItemText 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    margin: 0,
                    marginBottom: isPhone && schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ? 1 : undefined
                }}
                primary={
                    <Stack
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            flex: '1 1 auto'
                        }}>


                        <FormCheckbox
                            name={`${name}.${index}.selected`}
                            label={item.odt}
                            
                            sx={{ 
                                 marginRight: 0,
                                marginTop: 0,
                                flex: '0 0 50%',
                                alignSelf: isPhone && schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ? 'top' :  'center',
                                maxWidth: 400,
                                minWidth: !isPhone ? 200 : undefined,
                                overflow: isPhone ? 'hidden' : undefined
                            }}
                            typographyProps={{
                                sx: {
                                    fontSize: '.95rem',
                                    overflow: isPhone ? 'hidden' : undefined
                                }
                            }}

                            onChange={(_ev, checked) => onSelect(checked)}
                         />


                        {
                            schemaType === AttributeSchemaTypeEnum.PredefinedValue &&
                            renderSelect()
                        }

                        {
                            schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText && 
                            <Stack
                                sx={{
                                    display: 'flex',
                                    flexDirection: isPhone ? 'column' : 'row',
                                    flex: '1 1 auto'
                                }}>
                                {renderSelect()}
                                <Typography
                                    sx={{
                                        marginLeft: 1,
                                        marginRight: 1,
                                        alignSelf: 'center'
                                    }}>or</Typography>
                                {renderInput()}
                            </Stack>
                        }

                        {
                        (schemaType === AttributeSchemaTypeEnum.FreeformText ||
                            schemaType === AttributeSchemaTypeEnum.Numeric
                            ) &&

                            renderInput()
                    }

                    {
                        schemaType === AttributeSchemaTypeEnum.Date && 


                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DefaultDatePicker
                            value={date}
                            maxDate={new Date()}
                            slotProps={{ 
                                field: {clearable: true},
                                textField: { variant: 'filled', sx: {
                                    //minWidth: 300
                                    flexBasis: '50%',
                                    marginTop: 1,
                                    marginLeft: 1
                                }} 
                            }}
                            onAccept={(date: Date | null) => onDateChanged(date)}
                        />
                        </LocalizationProvider>
                    }

                    </Stack>
                } 
            
                slotProps={{
                    primary: {
                        component: 'div',
                        sx: {
                            display: 'flex',
                        }
                    }
                }} 
            />
        </ListItem>
    );
}